import { getAccountInfo, getRefundInfo } from '@/api';
import { IRefundInfoForm } from '@/types/common.type';
import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useAppDispatch } from './useReduxHooks';
import { CASH_PICKUP_NATIONS } from '@repo/constants';
import { isNationIncluded } from '@repo/utils';

const useGetAccountInfo = (
  nationality: string,
  passportNumber: string,
  setValue: UseFormSetValue<IRefundInfoForm>,
) => {
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedAccountInfo, setSelectedAccountInfo] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [isBankCardInquiry, setIsBankCardInquiry] = useState(false);
  const [isHkgNation, setIsHkgNation] = useState(false);
  const [isOpenLoad, setIsOpenLoad] = useState(false);
  const dispatch = useAppDispatch();

  const { data: beforeAccountInfo }: any = useQuery(
    'getAccountInfo',
    () => getAccountInfo(passportNumber),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setIsOpenLoad(true);
        }
      },
    },
  );

  const setAccountInfo = (data: any, nationality: string) => {
    const { accountInfo } = data;
    setValue('firstName', accountInfo.firstName);
    setValue('lastName', accountInfo.lastName);
    setValue('phoneNumber', accountInfo.phoneNumber);
    setValue('transferMethod', accountInfo.transferMethod);
    setValue('aliPayAccount', accountInfo.aliPayAccount);
    setValue('cardNumber', accountInfo.cardNumber);
    setValue('bank', accountInfo.bank);
    setValue('bankAccount', accountInfo.bankAccount);
    setValue('accountInfo', accountInfo.accountInfo);
    setValue('routingNumber', accountInfo.routingNumber);
    setValue('stateProvinceRegion', accountInfo.stateProvinceRegion);
    setValue('zipPostalCode', accountInfo.zipPostalCode);
    setValue('address', accountInfo.address);
    setValue('branchInformation', accountInfo.branchInformation);
    setValue('bsbNumber', accountInfo.bsbNumber);
    setValue('transitNumber', accountInfo.transitNumber);
    setValue('cashPickup', accountInfo.cashPickup);
    setValue('ibanNumber', accountInfo.ibanNumber);
    setValue('swiftCode', accountInfo.swiftCode);
    setValue('birth', accountInfo.birth);
    setValue('addressDetail', accountInfo.addressDetail);
    setValue('addressDetail2', accountInfo.addressDetail2);
    setValue('accountType', accountInfo.accountType);
    setValue('institutionNumber', accountInfo.institutionNumber);
    setValue('citizenId', accountInfo.citizenId);
    setValue('country', accountInfo.country);
    setValue('firstNameEng', accountInfo.firstNameEng);
    setValue('lastNameEng', accountInfo.lastNameEng);
    setValue('directInput', accountInfo.directInput);
    setValue('accountHolder', accountInfo.accountHolder);
    setValue('stateProvinceRegionEng', accountInfo.stateProvinceRegionEng);
    setValue('cityEng', accountInfo.cityEng);
    setValue('addressEng', accountInfo.addressEng);
    setValue('addressDetailEng', accountInfo.addressDetailEng);
    setSelectedAccountInfo(accountInfo.accountInfo);
    setSelectedAccountType(accountInfo.accountType);
    setSelectedBank(accountInfo.bank);
    setSelectedGender(accountInfo.gender);
    setIsBankCardInquiry(accountInfo.isBankCardInquiry || false);
    setIsHkgNation(accountInfo.isHkgNation || false);
    setSelectedRegion(accountInfo.stateProvinceRegion);
    setValue('city', accountInfo.city);
    setSelectedTown(accountInfo.city);
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: {
        email: data.email,
        hasNewAnswer: data.hasNewAnswer,
        isSwiftCodeRequired: data.isSwiftCodeRequired,
      },
    });
    if (isNationIncluded(CASH_PICKUP_NATIONS, nationality)) {
      setSelectedBank(accountInfo.cashPickup);
    }
  };

  const { refetch, isFetching } = useQuery<any, string>(
    'RefundInfo',
    () => getRefundInfo(passportNumber),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: async (data) => {
        if (!data) {
          return;
        }
        setAccountInfo(data, nationality);
      },
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      refetch();
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setValue('aliPayAccount', '');
    setValue('cardNumber', '');
    setValue('bank', '');
    setValue('bankAccount', '');
    setValue('accountInfo', '');
    setValue('routingNumber', '');
    setValue('city', '');
    setValue('stateProvinceRegion', '');
    setValue('zipPostalCode', '');
    setValue('address', '');
    setValue('branchInformation', '');
    setValue('bsbNumber', '');
    setValue('transitNumber', '');
    setValue('cashPickup', '');
    setValue('ibanNumber', '');
    setValue('swiftCode', '');
    setValue('birth', '');
    setValue('addressDetail', '');
    setValue('addressDetail2', '');
    setValue('accountType', '');
    setValue('institutionNumber', '');
    setValue('gender', '');
    setValue('citizenId', '');
    setValue('country', '');
    setValue('firstNameEng', '');
    setValue('lastNameEng', '');
    setValue('accountHolder', '');
    setIsBankCardInquiry(false);
    setSelectedBank('');
    setSelectedRegion('');
    setSelectedTown('');
    setSelectedGender('');
  }, [isHkgNation]);

  return {
    selectedGender,
    setSelectedGender,
    selectedAccountInfo,
    setSelectedAccountInfo,
    selectedAccountType,
    setSelectedAccountType,
    selectedBank,
    setSelectedBank,
    selectedRegion,
    setSelectedRegion,
    selectedTown,
    setSelectedTown,
    isBankCardInquiry,
    setIsBankCardInquiry,
    isHkgNation,
    setIsHkgNation,
    isOpenLoad,
    setIsOpenLoad,
    beforeAccountInfo,
    isFetching,
    setAccountInfo,
  };
};
export default useGetAccountInfo;
